import { useUIStore } from '../../store/uiStore';
import { useStructuresStore } from '../../store/structuresStore';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { PresentationFullscreen } from './slideshow/PresentationFullscreen';
import { PresentationPanel, type PresentationPanelContent } from './slideshow/PresentationPanel';
import { useSlideshowKeys } from './slideshow/useSlideshowKeys';
import type { SlideshowController } from './slideshow/useSlideshowController';
import type { SlideshowViewWatch } from './slideshow/useSlideshowViewWatch';

/**
 * Observation presentation for a Zoom call: zoom the map into an area, click
 * "Present observations" (the map-tools rail's Present row, MapRailTools.web,
 * or the Layers sheet's row), and run through the green (progress) photos
 * then the red (Opportunity) ones,
 * one big image at a time. A title card opens each category group so the
 * audience gets a narrative beat instead of a red photo appearing with no
 * warning after a run of green ones.
 *
 * Fullscreen-first everywhere (Sept 9 2026 client decision): opening the
 * presentation always goes straight to the overlay. Escape, the close
 * button, and the browser leaving fullscreen all end the presentation
 * outright. On the desktop shell, a Dock button in the overlay's chrome
 * reaches a panel beside the map instead (the same right-docked surface
 * used for a selected structure), because that panel is where the
 * update-on-pan flow lives; its own "Full screen" button goes back up.
 * Below the desktop shell there's no panel at all: closing the overlay ends
 * the presentation, full stop.
 *
 * The parts:
 *
 *   slideshow/deck.ts                 pure selection, ordering, URL ladders
 *   slideshow/stage.ts                pure panel <-> fullscreen transitions
 *   slideshow/useSlideshowController  deck position, autoplay, prefetch
 *   slideshow/useSlideshowKeys        every keystroke, in one effect
 *   slideshow/SlideStage              one deck item in a fixed dark stage
 *   slideshow/PresentationPanel       docked surface
 *   slideshow/PresentationFullscreen  overlay surface
 *
 * This file is only the composition. Map.web.tsx owns the controller, since
 * it is the thing that knows the map's bounds and can build a deck.
 *
 * The deck is a snapshot of the map at the moment Present was clicked, and
 * on the desktop shell the map stays live beside it. `view` is what the
 * panel needs to offer a rebuild once the presenter has moved off that area
 * (slideshow/useSlideshowViewWatch); fullscreen hides the map, so it has no
 * use for it.
 *
 * This is a PRESENTATION SURFACE, not a sheet — it never touches
 * `uiStore.activeSheet` or the sheet stack. Fullscreen borrows
 * `useSheetTabBarHold()` purely so the map's controls/legend/link hide
 * underneath it; the panel leaves the shell alone, the same as the structure
 * detail `DockedPanel` does.
 */
export function MapSlideshow({
  controller,
  view,
}: {
  controller: SlideshowController;
  view: SlideshowViewWatch;
}) {
  // Keys stay keyed to the live stage, so a panel that is sliding out
  // answers nothing.
  useSlideshowKeys(controller);

  // The panel slides out when the presentation ends. It unmounts at once
  // when the stage moves to fullscreen (a switch, not a leave) or when
  // another surface has taken the right dock; Map.web.tsx's
  // requestSlideshowClose ends the presentation in that case, after the
  // other surface's state is already set.
  const otherRightDock = useUIStore(
    (s) => s.dockedPostId != null || s.activeSheet != null || s.composerDockOpen
  );
  const structureSelected = useStructuresStore((s) => s.selectedStructure != null);
  const { current, photoIndex, photoCount, autoplay } = controller;
  const leave = useLeavingPanel<PresentationPanelContent>(
    controller.stage === 'panel',
    {
      current,
      photoIndex,
      photoCount,
      autoplay,
      inViewCount: view.inViewCount,
      viewMoved: view.viewMoved,
    },
    { instant: controller.stage === 'fullscreen' || otherRightDock || structureSelected }
  );

  if (controller.stage === 'fullscreen') return <PresentationFullscreen controller={controller} />;
  if (!leave.mounted || !leave.lastContent) return null;
  return (
    <PresentationPanel
      controller={controller}
      content={leave.lastContent}
      shown={leave.shown}
      onExited={leave.onExited}
    />
  );
}
